import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { impuestoService, Impuesto, ImpuestoCreate, ImpuestoUpdate } from '../../services/impuestoService';

interface ImpuestoFormProps {
  impuesto?: Impuesto | null;
  onClose: () => void;
}

const ImpuestoForm = ({ impuesto, onClose }: ImpuestoFormProps) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ImpuestoCreate | ImpuestoUpdate>({
    defaultValues: impuesto || {
      name: '',
      porcentaje: 0,
      tipo: 'iva',
      aplicaA: 'todos'
    }
  });

  useEffect(() => {
    if (impuesto) {
      reset({
        name: impuesto.name,
        porcentaje: impuesto.porcentaje,
        tipo: impuesto.tipo,
        aplicaA: impuesto.aplicaA
      });
    }
  }, [impuesto, reset]);

  const createMutation = useMutation(impuestoService.create, {
    onSuccess: () => {
      toast.success('Impuesto creado correctamente');
      queryClient.invalidateQueries(['impuestos']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear impuesto');
    }
  });

  const updateMutation = useMutation(
    (data: ImpuestoUpdate) => impuestoService.update(impuesto!.id, data),
    {
      onSuccess: () => {
        toast.success('Impuesto actualizado correctamente');
        queryClient.invalidateQueries(['impuestos']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar impuesto');
      }
    }
  );

  const onSubmit = (data: ImpuestoCreate | ImpuestoUpdate) => {
    if (impuesto) {
      updateMutation.mutate(data as ImpuestoUpdate);
    } else {
      createMutation.mutate(data as ImpuestoCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {impuesto ? 'Editar Impuesto' : 'Nuevo Impuesto'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Porcentaje (%) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register('porcentaje', {
                required: 'Porcentaje es requerido',
                min: { value: 0, message: 'Debe ser mayor o igual a 0' },
                max: { value: 100, message: 'Debe ser menor o igual a 100' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.porcentaje && (
              <p className="text-red-500 text-xs mt-1">{errors.porcentaje.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo *
            </label>
            <select
              {...register('tipo')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="iva">IVA</option>
              <option value="ieps">IEPS</option>
              <option value="ish">ISH</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aplica a *
            </label>
            <select
              {...register('aplicaA')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="todos">Todos</option>
              <option value="productos">Productos</option>
              <option value="servicios">Servicios</option>
            </select>
          </div>

          {impuesto && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              {impuesto ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImpuestoForm;

